/**
 * @author Gareth West
 * Really just adding promises and generics.
 */

/**
 * Create an IndexedDB instance.
 * @param name A unique database name.
 * @param version An integer to indicate change and help with data migration.
 * @param upgrader A migration (version change) handler.
 * @param blockedNotice A hook if upgrade is blocked by another tab.
 * @returns A DB instance
 */
export async function open(name: string, version?: number, upgrader?:Upgrader, blockedNotice?:()=>void, informer?: (info:Record<string,any>) => void, hardFailure = false) {
	const $ = new Promise<IDBDatabase | null>((resolve,reject) => {
		const req = window.indexedDB?.open(name, version);
		let upgraded = true; // `true` in case no upgrader is provided
		let succeeded = false;
		const check = () => upgraded && succeeded && resolve(req.result);
		if (!req) return resolve(null); // test environment
		if (upgrader) {
			req.onupgradeneeded = (e) => {
				upgraded = false;
				// console.log(`db ${name} migrating`);
				console.info(`IDB ${name} upgrading from ${e.oldVersion} to ${e.newVersion}.`);
				upgrader(e, name, () => {
					upgraded = true;
					// console.log(`db ${name} migrated`);
					check();
				});
			}
		}
		req.onblocked = e => {
			if (blockedNotice) blockedNotice();
			else console.error(`IDB ${name} v${version} blocked from upgrading by an open connection.`, e);
		};
		req.onerror = e => {
			const msg = `IDB ${name} v${version} failed to open. Upgraded: ${upgraded ? 'yes' : 'no'}.`;
			if (hardFailure) reject(new Error(msg));
			else console.error(msg, e);
			resolve(null);
		};
		req.onsuccess = e => {
			succeeded = true;
			// console.log(`db ${name} opened`);
			check();
		};
	});
	if (informer) {
		const db = await $;
		if (db) informer(await dumpCountsInto({}, db));
		return db;
	}
	return $;
}

export type Upgrader = (e: IDBVersionChangeEvent, dbName: string, done?:()=>void) => void;

/**
 * A function that is useful for creating a migration handler with a `done` callback
 * useful for preventing object store transactions on an obejct store that has not been
 * created yet.
 * @param migrations A list of migration functions to be called per version.
 * Eg. `{ 2: (db)=>[db.createObjectStore(...)], 7: (db)=>{...} }`
 * Each migration function should optionally return an array of new object stores.
 * @returns An onupgradeneeded handler with a `done` callback that will be called when all
 * object store transactions have completed.
 */
export function createUpgrader(migrations: Record<number, (db:IDBDatabase) => (IDBObjectStore[] | void)>):Upgrader {
	return (e: IDBVersionChangeEvent, dbName: string, onOSTransactionsCompleted?:()=>void) => {
		const db = (e.target as IDBOpenDBRequest).result;
		if (!db) return console.error(`IDB ${dbName} cannot upgrade.`);
		db.onerror = e => console.error(`IDB ${dbName} error while upgrading.`, e);
		const prev = e.oldVersion || 0;
		const ver = e.newVersion || 0; // unlikely

		const check = (idx = -1) => {
			// console.log('[!] check '+idx, osList.map(([os,d]) => d ? 'done' : 'not done').join(', '))
			// TESTED ON CHROME AND FF - ONLY THE LAST OS' TRANSACTION COMPLETES
			// return (osList.filter(([,d]) => !d).length === 0) && done?.()
			onOSTransactionsCompleted?.();
		};
		const osList:[IDBObjectStore,boolean][] = [];
		for (let i = prev+1; i <= ver; i++) {
			if (migrations[i]) {
				try {
					const stores = migrations[i](db);
					if (stores) osList.push(...stores.map(os => [os,false] as [IDBObjectStore,boolean]));
				} catch (error) {
					console.error(`Migration ${i} on ${db.name} failed.`, error);
				}
			}
		}
		if (osList.length === 0) onOSTransactionsCompleted?.();
		else osList.forEach((item,i) => item[0].transaction.oncomplete = () => { item[1] = true; check(i); });
	}
}

/**
 * Delete an IndexedDB instance.
 * @param name A unique database name.
 * @returns The deleted IndexedDB should really be undefined at this stage.
 */
export async function delDB(name: string) {
	return new Promise<IDBDatabase | null>((resolve,reject) => {
		const req = window.indexedDB?.deleteDatabase(name);
		if (!req) return resolve(null); // test environment
		req.onerror = reject;
		req.onsuccess = e => resolve(req.result); // should be undefined
	});
}

export interface Transaction extends IDBTransaction {
	os: (name: string) => ObjectStore;
}

export function transaction(db: IDBDatabase, storeNames: string | Iterable<string>, mode?: IDBTransactionMode) {
	const t = db.transaction(storeNames, mode) as Transaction;
	t.os = (name) => new ObjectStore(t.objectStore(name));
	return t;
}

export async function transact(transaction:IDBTransaction) {
	return new Promise<Event>((resolve,reject) => {
		transaction.onerror = reject;
		transaction.oncomplete = resolve;
	})
}

async function count(os:IDBObjectStore|IDBIndex, query?: IDBValidKey | IDBKeyRange) {
	return new Promise<number>((resolve,reject) => {
		const req = os.count(query);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function add(os:IDBObjectStore, value:any, key?:IDBValidKey) {
	return new Promise<IDBValidKey>((resolve,reject) => {
		const req = os.add(value,key);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function put(os:IDBObjectStore, value:any, key?:IDBValidKey) {
	return new Promise<IDBValidKey>((resolve,reject) => {
		const req = os.put(value,key);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function del(os:IDBObjectStore, query: IDBValidKey | IDBKeyRange) {
	return new Promise<void>((resolve,reject) => {
		const req = os.delete(query);
		req.onerror = reject;
		req.onsuccess = () => resolve();
	})
}
async function get<T>(os:IDBObjectStore|IDBIndex, query: IDBValidKey | IDBKeyRange) {
	return new Promise<T>((resolve,reject) => {
		const req = os.get(query);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function getKey(os:IDBObjectStore|IDBIndex, query: IDBValidKey | IDBKeyRange) {
	return new Promise<IDBValidKey|undefined>((resolve,reject) => {
		const req = os.getKey(query);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function getAll<T>(os:IDBObjectStore|IDBIndex, query?: IDBValidKey | IDBKeyRange | null, count?: number) {
	return new Promise<T[]>((resolve,reject) => {
		const req = os.getAll(query, count);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function getAllKeys(os:IDBObjectStore|IDBIndex, query?: IDBValidKey | IDBKeyRange | null, count?: number) {
	return new Promise<IDBValidKey[]>((resolve,reject) => {
		const req = os.getAllKeys(query, count);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function openCursor(os:IDBObjectStore|IDBIndex, query?: IDBValidKey | IDBKeyRange | null, direction?: IDBCursorDirection) {
	return new Promise<IDBCursorWithValue|null>((resolve,reject) => {
		const req = os.openCursor(query, direction);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function openKeyCursor(os:IDBObjectStore|IDBIndex, query?: IDBValidKey | IDBKeyRange | null, direction?: IDBCursorDirection) {
	return new Promise<IDBCursor|null>((resolve,reject) => {
		const req = os.openKeyCursor(query, direction);
		req.onerror = reject;
		req.onsuccess = () => resolve(req.result);
	})
}
async function clear(os:IDBObjectStore) {
	return new Promise((resolve,reject) => {
		const req = os.clear();
		req.onerror = reject;
		req.onsuccess = () => resolve(null);
	})
}

export class ObjectStore {
	constructor(
		private os: IDBObjectStore,
	){}
	get keyPath() {
		return this.os.keyPath;
	}
	index(name: string) {
		return new Index(this.os.index(name));
	}
	count(query?: IDBValidKey | IDBKeyRange) {
		return count(this.os, query);
	}
	add(value:any, key?:IDBValidKey) {
		return add(this.os, value, key);
	}
	put(value:any, key?:IDBValidKey) {
		return put(this.os, value, key);
	}
	del(query: IDBValidKey | IDBKeyRange) {
		return del(this.os, query);
	}
	get<T>(query: IDBValidKey | IDBKeyRange) {
		return get<T>(this.os, query);
	}
	getKey(query: IDBValidKey | IDBKeyRange) {
		return getKey(this.os, query);
	}
	getAll<T>(query?: IDBValidKey | IDBKeyRange | null, count?: number) {
		return getAll<T>(this.os, query, count);
	}
	getAllKeys(query?: IDBValidKey | IDBKeyRange | null, count?: number) {
		return getAllKeys(this.os, query, count);
	}
	openCursor(query?: IDBValidKey | IDBKeyRange | null, direction?: IDBCursorDirection) {
		return openCursor(this.os, query, direction);
	}
	openKeyCursor(query?: IDBValidKey | IDBKeyRange | null, direction?: IDBCursorDirection) {
		return openKeyCursor(this.os, query, direction);
	}
	clear() {
		return clear(this.os);
	}
}

export class Index {
	constructor(
		private index: IDBIndex,
	) {}
	count(query: IDBValidKey | IDBKeyRange) {
		return count(this.index, query);
	}
	get<T>(query: IDBValidKey | IDBKeyRange) {
		return get<T>(this.index, query);
	}
	getKey(query: IDBValidKey | IDBKeyRange) {
		return getKey(this.index, query);
	}
	getAll<T>(query?: IDBValidKey | IDBKeyRange | null, count?: number) {
		return getAll<T>(this.index, query, count);
	}
	getAllKeys(query?: IDBValidKey | IDBKeyRange | null, count?: number) {
		return getAllKeys(this.index, query, count);
	}
	openCursor(query?: IDBValidKey | IDBKeyRange | null, direction?: IDBCursorDirection) {
		return openCursor(this.index, query, direction);
	}
	openKeyCursor(query?: IDBValidKey | IDBKeyRange | null, direction?: IDBCursorDirection) {
		return openKeyCursor(this.index, query, direction)
	}
}




/**
 * A helper function that iterates over the object stores in a database and lists the records in each.
 */
export async function dumpCountsInto(obj: Record<string,any>, db: IDBDatabase) {
	for (const name of db.objectStoreNames) {
		const os = transaction(db, name).os(name);
		const count = await os.count();
		obj[name] = count;
	}
	return obj;
}
